import { Link } from 'react-router-dom';
import { useStore } from '@/store/useStore';
import type { Product } from '@/types/product';

interface ProductCardProps {
  product: Product;
  reason?: string;
  relevanceScore?: number;
}

export default function ProductCard({ product, reason, relevanceScore }: ProductCardProps) {
  const addToWishlist = useStore(state => state.addToWishlist);
  const removeFromWishlist = useStore(state => state.removeFromWishlist);
  const isInWishlist = useStore(state => state.isInWishlist(product.id));

  const hasStock = Object.values(product.stock).some(qty => qty > 0);
  const isLowStock = hasStock && Object.values(product.stock).every(qty => qty <= 3);

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isInWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  return (
    <Link to={`/product/${product.id}`} className="group block">
      <div className="bg-white rounded-lg overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 transform hover:-translate-y-1">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {product.isNew && (
              <span className="bg-amber text-white text-xs font-bold px-2 py-1 rounded">NEW</span>
            )}
            {product.isSale && (
              <span className="bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">SALE</span>
            )}
            {product.isBestseller && (
              <span className="bg-forest text-warm text-xs font-bold px-2 py-1 rounded">BESTSELLER</span>
            )}
            {!hasStock && (
              <span className="bg-gray-600 text-white text-xs font-bold px-2 py-1 rounded">OUT OF STOCK</span>
            )}
            {isLowStock && hasStock && (
              <span className="bg-orange-600 text-white text-xs font-bold px-2 py-1 rounded">LOW STOCK</span>
            )}
          </div>

          {/* Wishlist button */}
          <button
            onClick={handleWishlistClick}
            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <svg
              className={`w-5 h-5 ${isInWishlist ? 'fill-red-500 text-red-500' : 'fill-none text-gray-600'}`}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>

          {/* Relevance score (AI mode) */}
          {relevanceScore !== undefined && (
            <div className="absolute bottom-3 right-3 bg-amber text-white text-xs font-bold px-2 py-1 rounded">
              {Math.round(relevanceScore * 100)}% match
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">{product.brand}</p>
              <h3 className="font-semibold text-slate text-sm line-clamp-2 mb-1">{product.name}</h3>
            </div>
          </div>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-2">
            <div className="flex text-amber">
              {[...Array(5)].map((_, i) => (
                <svg
                  key={i}
                  className={`w-3 h-3 ${i < Math.floor(product.rating) ? 'fill-current' : 'fill-gray-300'}`}
                  viewBox="0 0 20 20"
                >
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                </svg>
              ))}
            </div>
            <span className="text-xs text-gray-500">({product.reviewCount})</span>
          </div>

          {/* AI Reason */}
          {reason && (
            <p className="text-xs text-forest-light italic mb-2 line-clamp-2">
              {reason}
            </p>
          )}

          {/* Price */}
          <div className="flex items-baseline gap-2">
            {product.salePrice ? (
              <>
                <span className="text-lg font-bold text-red-600">£{product.salePrice.toFixed(2)}</span>
                <span className="text-sm text-gray-500 line-through">£{product.price.toFixed(2)}</span>
              </>
            ) : (
              <span className="text-lg font-bold text-slate">£{product.price.toFixed(2)}</span>
            )}
          </div>

          {/* Colours */}
          {product.colours && product.colours.length > 0 && (
            <div className="flex gap-1 mt-2">
              {product.colours.slice(0, 5).map((colour, i) => (
                <div
                  key={i}
                  className="w-4 h-4 rounded-full border border-gray-300"
                  style={{ backgroundColor: colour.toLowerCase() }}
                  title={colour}
                />
              ))}
              {product.colours.length > 5 && (
                <span className="text-xs text-gray-500">+{product.colours.length - 5}</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
